import React, { useState, useEffect } from "react";
import "../Settings/AccountDetails.css";
import { toast } from "react-toastify";
import { fetchOwnAccount } from "../../features/users/api/accountApi";
import useSaveOwnAccount from "../../features/users/hooks/useSaveOwnAccount";



export default function UserAccountDetails() {
  const mapResponseToFormData = (payload) => {
    if (!payload) return {};
    return {
      firstName: payload.first_name || "",
      lastName: payload.last_name || "",
      email: payload.email || "",
      phone: payload.phone || "",
      address: payload.address?.address_line || "",
      city: payload.address?.city || "",
      state: payload.address?.state || "",
      country: payload.address?.country || "",
      zip: payload.address?.zip_code || "",
      role: payload.role ?? null,
      orgBranch: payload.branch_name ?? payload.organization_name ?? null,
    };
  };
  // State to manage form data
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zip: "",
    role: null,
    orgBranch: null,
  });

  const [disableEdit, setDisableEdit] = useState(true);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [refreshList, setRefreshList] = useState(0);
  const [userId, setUserId] = useState(null);

  const [originalFormData, setOriginalFormData] = useState({});
  const token = sessionStorage.getItem("accessToken");
  const sessionUserId = sessionStorage.getItem("UserID");
  const { saveOwnAccount } = useSaveOwnAccount({
    accessToken: token,
    userId: sessionUserId,
  });

  // Fetch user details on demand
  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const user = await fetchOwnAccount({
        accessToken: token,
        userId: sessionUserId,
      });

      if (user) {
        const initialData = mapResponseToFormData(user);
        setFormData(initialData);
        setOriginalFormData(initialData);
        setUserId(user.id ?? null);
      } else {
        setFetchError("No  data found for the provided email.");
      }
    } catch (error) {
      console.error("Fetch user details failed:", error);
      setFetchError(error.message || "Failed to fetch details");
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch when refreshList changes
  useEffect(() => {
    if (refreshList > 0) {
      fetchUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshList]);

  // Handle input changes for the form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Validate form data before submitting
  const validate = () => {
    const newErrors = {};
    const namePattern = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/; // Updated to allow diverse names

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First Name is required";
    } else if (!namePattern.test(formData.firstName)) {
      newErrors.firstName = "First Name must contain only alphabets";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last Name is required";
    } else if (!namePattern.test(formData.lastName)) {
      newErrors.lastName = "Last Name must contain only alphabets";
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailPattern.test(formData.email)) {
      newErrors.email = "Email is not valid";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Contact No. is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    } else if (/^0+$/.test(formData.phone.trim())) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Update user details
  const updateUserDetails = async () => {
    if (!validate()) return;
    if (!userId) {
      setFetchError("Unable to update: user ID not loaded yet.");
      return;
    }
    let timeoutId;
    setFetchError(null);
    setSaving(true);
    try {
      // A Customer saves through PUT /users/, Staff through PUT /users/update/
      // (features/users/domain/accountSelfEdit.js). Either way: the user record.
      const updatedUser = await saveOwnAccount({
        role: formData.role,
        selfId: userId,
        formData,
      });
      const updatedFormData = mapResponseToFormData(updatedUser);
      setFormData(updatedFormData);
      setOriginalFormData(updatedFormData);
      if (updatedUser?.id != null) {
        setUserId(updatedUser.id);
      }

      setRefreshList((v) => v + 1);

      setSuccessMessage("Account details updated successfully!");
      timeoutId = setTimeout(() => {
        setSuccessMessage("");
      }, 3000);

      setDisableEdit(true);
    } catch (error) {
      console.error("Update Account details failed:", error);
      setFetchError(error.message || "Failed to update details");
    } finally {
      setSaving(false);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  };

  return (
    <div>
      <h2> Account Details</h2>
      {loading && <p style={{ color: "red" }}>Loading  details...</p>}
      {fetchError && <p className="error-message">{fetchError}</p>}

      <form>
        <div className="form-row">
          <div className="form-group">
            <label>
              First Name <span className="required-asterisk">*</span>
            </label>
            <input
              type="text"
              maxLength={50}
              placeholder="Enter First Name"
              className="form-control"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              disabled={disableEdit || saving}
            />
            {errors.firstName && (
              <span className="error-message">{errors.firstName}</span>
            )}
          </div>
          <div className="form-group">
            <label>
              Last Name <span className="required-asterisk">*</span>
            </label>
            <input
              type="text"
              maxLength={50}
              placeholder="Enter Last Name"
              className="form-control"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              disabled={disableEdit || saving}
            />
            {errors.lastName && (
              <span className="error-message">{errors.lastName}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              placeholder="Enter email address"
              className="form-control"
              name="email"
              value={formData.email}
              disabled
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>
          <div className="form-group">
            <label>
              Contact No. <span className="required-asterisk">*</span>
            </label>
            <input
              type="text"
              maxLength={10}
              className="form-control"
              name="phone"
              value={formData.phone}
              placeholder="Enter the 10 digit number"
              onChange={handleInputChange}
              disabled={disableEdit || saving}
            />
            {errors.phone && (
              <span className="error-message">{errors.phone}</span>
            )}
          </div>
        </div>
        <div className="button-container">
          {!disableEdit && (
            <button
              type="button"
              className={`btn-cancel ${
                saving ? "employee-button-disabled" : ""
              }`}
              onClick={() => {
                setDisableEdit(true);
                setErrors({});
                setFormData({
                  firstName: originalFormData.firstName,
                  lastName: originalFormData.lastName,
                  email: originalFormData.email,
                  phone: originalFormData.phone,
                  address: originalFormData.address,
                  city: originalFormData.city,
                  state: originalFormData.state,
                  country: originalFormData.country,
                  zip: originalFormData.zip,
                  utilityCompany: originalFormData.utilityCompany,
                  utilityAccountNumber: originalFormData.utilityAccountNumber,
                  role: originalFormData.role,
                  orgBranch: originalFormData.orgBranch,
                });
              }}
              disabled={saving}
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            className={`btn-edit ${saving ? "employee-button-disabled" : ""}`}
            onClick={() =>
              disableEdit ? setDisableEdit(false) : updateUserDetails()
            }
            disabled={saving}
          >
            {!disableEdit && saving ? (
              <>
                <span className="btn-spinner" /> Saving...
              </>
            ) : (
              disableEdit ? "Edit" : "Save"
            )}
          </button>
        </div>
        {successMessage && (
          <p className="success-message" style={{ color: "green" }}>
            {successMessage}
          </p>
        )}
      </form>
    </div>
  );
}
